import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, Plus, X, Edit3 } from "lucide-react";

const ESTADOS = ["reportado", "en_proceso", "aprobado", "rechazado", "pagado", "cerrado"];
const TIPOS = ["incendio", "sismo", "robo", "dano_agua", "vehiculo", "lesion", "responsabilidad_civil", "otro"];

export default function AdminSiniestros() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/siniestros");
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter ? items.filter((s) => s.estado === filter) : items;
  const totalReclamado = items.reduce((sum, s) => sum + (parseFloat(s.monto_reclamado_uf) || 0), 0);
  const totalPagado = items.reduce((sum, s) => sum + (parseFloat(s.monto_pagado_uf) || 0), 0);
  const activos = items.filter((s) => ["reportado","en_proceso","aprobado"].includes(s.estado)).length;

  const updateEstado = async (id, estado) => {
    try { await http.patch(`/admin/siniestros/${id}`, { estado }); toast.success("Actualizado"); load(); }
    catch { toast.error("Error"); }
  };

  return (
    <div data-testid="admin-siniestros">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Siniestros</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Reclamos en gestión y resueltos.</p>
        </div>
        <button className="btn-primary" onClick={() => setModalOpen(true)} data-testid="btn-new-siniestro-global"><Plus size={16}/>Nuevo siniestro</button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <StatCard label="Activos" value={activos} color="#F57C00"/>
        <StatCard label="Reclamado total" value={`UF ${totalReclamado.toFixed(1)}`} color="var(--brand-navy)"/>
        <StatCard label="Pagado total" value={`UF ${totalPagado.toFixed(1)}`} color="#2E7D32"/>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        <button onClick={() => setFilter("")} className={`yn-btn ${filter === "" ? "yn-btn--active-yes" : ""}`}>Todos</button>
        {ESTADOS.map((e) => (
          <button key={e} onClick={() => setFilter(e)} className={`yn-btn ${filter === e ? "yn-btn--active-yes" : ""}`} data-testid={`filter-${e}`}>{e}</button>
        ))}
      </div>

      <div className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: "var(--border-subtle)" }}>
        {loading ? <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Cargando…</div>
         : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <AlertTriangle size={36} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }}/>
            <div className="font-bold mb-1" style={{ fontFamily: "Manrope" }}>Sin siniestros</div>
            <div className="text-sm" style={{ color: "var(--text-muted)" }}>Cuando reportes un siniestro aparecerá aquí.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead><tr><th>Cliente</th><th>Tipo</th><th>Fecha</th><th>Reclamado</th><th>Aprobado</th><th>Pagado</th><th>Estado</th><th>Aseguradora</th><th></th></tr></thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} data-testid={`siniestro-${s.id}`}>
                    <td className="font-semibold">
                      {s.cliente_id ? (
                        <Link to={`/admin/comunidades/${s.cliente_id}`} className="nav-link">{s.cliente_nombre}</Link>
                      ) : s.cliente_nombre}
                      <div className="text-[11px] capitalize" style={{ color: "var(--text-muted)" }}>{s.cliente_tipo}</div>
                    </td>
                    <td className="capitalize">{s.tipo_siniestro?.replace(/_/g," ")}</td>
                    <td className="text-[13px]">{s.fecha_siniestro}</td>
                    <td>UF {Number(s.monto_reclamado_uf || 0).toFixed(1)}</td>
                    <td>UF {Number(s.monto_aprobado_uf || 0).toFixed(1)}</td>
                    <td className="font-bold" style={{ color: s.monto_pagado_uf ? "#2E7D32" : "var(--text-muted)" }}>UF {Number(s.monto_pagado_uf || 0).toFixed(1)}</td>
                    <td>
                      <select className="field-input" style={{ padding: "0.3rem 0.5rem", fontSize: 12, width: 130 }} value={s.estado} onChange={(e) => updateEstado(s.id, e.target.value)}>
                        {ESTADOS.map((e) => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </td>
                    <td className="text-[13px]">{s.aseguradora || "—"}</td>
                    <td><button onClick={() => setEditing(s)} style={{ color: "var(--brand-accent)" }}><Edit3 size={14}/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && <NewSiniestroModal onClose={() => setModalOpen(false)} onCreated={() => { load(); setModalOpen(false); }}/>}
      {editing && <EditSiniestroModal item={editing} onClose={() => setEditing(null)} onSaved={() => { load(); setEditing(null); }}/>}
    </div>
  );
}

const StatCard = ({ label, value, color }) => (
  <div className="p-5 bg-white border rounded-xl" style={{ borderColor: "var(--border-subtle)" }}>
    <div className="w-10 h-10 rounded-md flex items-center justify-center mb-3" style={{ background: color + "22" }}>
      <AlertTriangle size={18} style={{ color }}/>
    </div>
    <div className="text-2xl md:text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>{value}</div>
    <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{label}</div>
  </div>
);

function NewSiniestroModal({ onClose, onCreated }) {
  const [clientes, setClientes] = useState([]);
  const [form, setForm] = useState({
    cliente_id: "", fecha_siniestro: new Date().toISOString().slice(0,10),
    tipo_siniestro: "incendio", descripcion: "", ubicacion: "",
    monto_reclamado_uf: "", aseguradora: "", ejecutivo_asignado: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => { http.get("/admin/clientes").then((r) => setClientes(r.data || [])); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await http.post("/admin/siniestros", { ...form, monto_reclamado_uf: parseFloat(form.monto_reclamado_uf) || 0 });
      toast.success("Siniestro registrado");
      onCreated();
    } catch { toast.error("Error"); }
  };

  return (
    <Wrap title="Nuevo siniestro" onClose={onClose} testid="new-siniestro-modal">
      <form onSubmit={submit}>
        <div className="p-5 space-y-4">
          <Fld label="Cliente *">
            <select className="field-input" value={form.cliente_id} onChange={(e) => set("cliente_id", e.target.value)} required data-testid="new-sin-cliente">
              <option value="">Seleccionar cliente...</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre} · {c.tipo}</option>)}
            </select>
          </Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Fecha *"><input type="date" className="field-input" value={form.fecha_siniestro} onChange={(e) => set("fecha_siniestro", e.target.value)} required/></Fld>
            <Fld label="Tipo *">
              <select className="field-input" value={form.tipo_siniestro} onChange={(e) => set("tipo_siniestro", e.target.value)} required>
                {TIPOS.map((t) => <option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
              </select>
            </Fld>
          </div>
          <Fld label="Descripción *"><textarea rows={3} className="field-input" value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} required/></Fld>
          <Fld label="Ubicación"><input className="field-input" value={form.ubicacion} onChange={(e) => set("ubicacion", e.target.value)}/></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Monto reclamado UF"><input type="number" step="0.1" min="0" className="field-input" value={form.monto_reclamado_uf} onChange={(e) => set("monto_reclamado_uf", e.target.value)}/></Fld>
            <Fld label="Aseguradora"><input className="field-input" value={form.aseguradora} onChange={(e) => set("aseguradora", e.target.value)}/></Fld>
          </div>
          <Fld label="Ejecutivo asignado"><input className="field-input" value={form.ejecutivo_asignado} onChange={(e) => set("ejecutivo_asignado", e.target.value)}/></Fld>
        </div>
        <div className="p-5 border-t flex justify-end gap-3 sticky bottom-0 bg-white" style={{ borderColor: "var(--border-subtle)" }}>
          <button type="button" className="btn-outline" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" data-testid="new-sin-submit">Registrar</button>
        </div>
      </form>
    </Wrap>
  );
}

function EditSiniestroModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    estado: item.estado,
    monto_aprobado_uf: item.monto_aprobado_uf || "",
    monto_pagado_uf: item.monto_pagado_uf || "",
    fecha_pago: item.fecha_pago || "",
    aseguradora: item.aseguradora || "",
    ejecutivo_asignado: item.ejecutivo_asignado || "",
    notas: item.notas || "",
    descripcion: item.descripcion || "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await http.patch(`/admin/siniestros/${item.id}`, {
        ...form,
        monto_aprobado_uf: parseFloat(form.monto_aprobado_uf) || 0,
        monto_pagado_uf: parseFloat(form.monto_pagado_uf) || 0,
        fecha_pago: form.fecha_pago || null,
      });
      toast.success("Actualizado");
      onSaved();
    } catch { toast.error("Error"); }
  };

  return (
    <Wrap title="Gestionar siniestro" subtitle={`${item.tipo_siniestro?.replace(/_/g," ")} · ${item.fecha_siniestro}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="p-5 space-y-4">
          <Fld label="Estado">
            <select className="field-input" value={form.estado} onChange={(e) => set("estado", e.target.value)}>
              {ESTADOS.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Aprobado UF"><input type="number" step="0.1" min="0" className="field-input" value={form.monto_aprobado_uf} onChange={(e) => set("monto_aprobado_uf", e.target.value)}/></Fld>
            <Fld label="Pagado UF"><input type="number" step="0.1" min="0" className="field-input" value={form.monto_pagado_uf} onChange={(e) => set("monto_pagado_uf", e.target.value)}/></Fld>
          </div>
          <Fld label="Fecha de pago"><input type="date" className="field-input" value={form.fecha_pago} onChange={(e) => set("fecha_pago", e.target.value)}/></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Aseguradora"><input className="field-input" value={form.aseguradora} onChange={(e) => set("aseguradora", e.target.value)}/></Fld>
            <Fld label="Ejecutivo"><input className="field-input" value={form.ejecutivo_asignado} onChange={(e) => set("ejecutivo_asignado", e.target.value)}/></Fld>
          </div>
          <Fld label="Descripción"><textarea rows={2} className="field-input" value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)}/></Fld>
          <Fld label="Notas internas"><textarea rows={2} className="field-input" value={form.notas} onChange={(e) => set("notas", e.target.value)}/></Fld>
        </div>
        <div className="p-5 border-t flex justify-end gap-3 sticky bottom-0 bg-white" style={{ borderColor: "var(--border-subtle)" }}>
          <button type="button" className="btn-outline" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary">Guardar</button>
        </div>
      </form>
    </Wrap>
  );
}

const Wrap = ({ title, subtitle, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10,37,64,0.55)" }} onClick={onClose}>
    <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
      <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10" style={{ borderColor: "var(--border-subtle)" }}>
        <div>
          <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>{title}</h3>
          {subtitle && <div className="text-xs capitalize" style={{ color: "var(--text-muted)" }}>{subtitle}</div>}
        </div>
        <button onClick={onClose}><X size={20}/></button>
      </div>
      {children}
    </div>
  </div>
);

const Fld = ({ label, children }) => (
  <div>
    <label className="field-label">{label}</label>
    {children}
  </div>
);
